import * as THREE from 'three';
import type { WallType } from '../types/floorplannerTypes';

export type MeasurementOutlineExtent = {
  startT: number;
  endT: number;
  length: number;
};

export type MeasurementLineSpan = {
  startT: number;
  endT: number;
  length: number;
  hiddenStart: number;
  hiddenEnd: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const calculateWallMeasurementSpan = (
  wallLength: number,
  leftExtent?: MeasurementOutlineExtent | null,
  rightExtent?: MeasurementOutlineExtent | null
): MeasurementLineSpan => {
  if (!Number.isFinite(wallLength) || wallLength <= 1e-6) {
    return {
      startT: 0,
      endT: 0,
      length: 0,
      hiddenStart: 0,
      hiddenEnd: 0,
    };
  }

  let startT = 0;
  let endT = wallLength;

  if (leftExtent && rightExtent) {
    startT = Math.max(leftExtent.startT, rightExtent.startT);
    endT = Math.min(leftExtent.endT, rightExtent.endT);
  } else {
    const measurementExtent = leftExtent ?? rightExtent;
    if (measurementExtent) {
      startT = measurementExtent.startT;
      endT = measurementExtent.endT;
    }
  }

  if (endT < startT) {
    startT = 0;
    endT = wallLength;
  }

  const clampedStartT = clamp(startT, 0, wallLength);
  const clampedEndT = clamp(endT, clampedStartT, wallLength);

  return {
    startT: clampedStartT,
    endT: clampedEndT,
    length: clampedEndT - clampedStartT,
    hiddenStart: clampedStartT,
    hiddenEnd: wallLength - clampedEndT,
  };
};

export const calculateResolvedEdgeConnectionMeasurementSpan = (
  wall: WallType,
  wallLength: number,
): MeasurementLineSpan | null => {
  if (!Number.isFinite(wallLength) || wallLength <= 1e-6 || !wall.edgeConnections?.length) {
    return null;
  }

  const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
  if (direction.lengthSq() <= 1e-12) {
    return null;
  }
  direction.normalize();

  let startT = 0;
  let endT = wallLength;
  let hasResolvedEndpoint = false;

  wall.edgeConnections.forEach((connection) => {
    const targetEdgePoint = connection.targetEdgePoint;
    if (!targetEdgePoint) {
      return;
    }

    const resolvedT = clamp(
      new THREE.Vector2().subVectors(targetEdgePoint, wall.start).dot(direction),
      0,
      wallLength,
    );

    if (connection.sourceEndpoint === 'start') {
      startT = Math.max(startT, resolvedT);
      hasResolvedEndpoint = true;
    } else if (connection.sourceEndpoint === 'end') {
      endT = Math.min(endT, resolvedT);
      hasResolvedEndpoint = true;
    }
  });

  if (!hasResolvedEndpoint || endT < startT) {
    return null;
  }

  return {
    startT,
    endT,
    length: endT - startT,
    hiddenStart: startT,
    hiddenEnd: wallLength - endT,
  };
};
